/**
 * Location-based store suggestion. Companion of snippets/geo-switch.liquid; the rules are in
 * GEO-STORE-SWITCH-PLAN.md (workspace plans/ folder) section 3.
 *
 * Once per visitor (cookie `geo_switch`), after the page has loaded:
 *   - detect the visitor's country through /browsing_context_suggestions.json;
 *   - country unknown or outside the markets: nothing, unless the browser-language fallback is on;
 *   - country served by this store with another currency: switch to it silently (one reload);
 *   - country served by the other store: open the dialog. Navigation only happens on a click.
 * A manual use of any localization form wins for a year. Arriving from the sibling store (referrer
 * or the `geo` marker in the URL) counts as a decision, so the destination never asks again.
 * `?geo_debug=SE` simulates a detected country on previews, `?geo_debug=none` an unknown one.
 *
 * Plain module, no dependency on the Horizon runtime: the same file runs on Savor, Theme A and B.
 */

const COOKIE = 'geo_switch';
const SESSION_KEY = 'geo_switch_checked';
const MARKER = 'geo';
const MANUAL_DAYS = 365;

const dialog = document.getElementById('GeoSwitch');

if (dialog instanceof HTMLDialogElement) {
  init(dialog);
}

/**
 * @param {HTMLDialogElement} dialog
 */
function init(dialog) {
  const data = dialog.dataset;
  const store = data.store === 'pl' ? 'pl' : 'eu';
  const current = (data.country || '').toUpperCase();
  const currency = (data.currency || '').toUpperCase();
  const days = Math.max(1, Number(data.days) || 30);
  const languageFallback = data.languageFallback === 'true';
  const fallbackCountry = (data.fallbackCountry || 'AT').toUpperCase();
  const siblingOrigin = originOf(data.siblingOrigin || '');
  /** @type {Record<string, string>} */
  let currencies = {};
  try {
    currencies = JSON.parse(data.currencies || '{}');
  } catch {
    currencies = {};
  }

  const form = dialog.querySelector('form');
  const countryInput = dialog.querySelector('[data-geo-country-input]');
  if (!(form instanceof HTMLFormElement) || !(countryInput instanceof HTMLInputElement)) return;

  // 1. A manual choice in any localization form (the pickers) is remembered for a year.
  document.addEventListener(
    'submit',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLFormElement) || target === form) return;
      if (/\/localization\/?$/.test(target.getAttribute('action') || '')) {
        writeCookie(COOKIE, 'manual', MANUAL_DAYS);
      }
    },
    true
  );

  // 2. Arriving from the sibling store after a "go" click: never ask again here.
  const url = new URL(window.location.href);
  const hasMarker = url.searchParams.has(MARKER);
  if (hasMarker) {
    url.searchParams.delete(MARKER);
    const clean = url.pathname + (url.searchParams.size ? `?${url.searchParams}` : '') + url.hash;
    window.history.replaceState(window.history.state, '', clean);
  }
  const fromSibling = siblingOrigin !== '' && originOf(document.referrer) === siblingOrigin;
  if (hasMarker || fromSibling) {
    writeCookie(COOKIE, 'go', days);
    return;
  }

  // 3. Already decided (stay, go, auto, manual).
  if (readCookie(COOKIE)) return;

  // 4. Nothing applied earlier in this session; check again next session.
  const debug = url.searchParams.get('geo_debug');
  if (!debug && sessionGet(SESSION_KEY)) return;

  const detection = debug ? Promise.resolve(simulated(debug)) : detect(current);
  const loaded = new Promise((resolve) => {
    if (document.readyState === 'complete') resolve(undefined);
    else window.addEventListener('load', () => resolve(undefined), { once: true });
  });

  Promise.all([detection, loaded]).then(([detected]) => {
    decide(detected);
  });

  /**
   * @param {{ country: string, name: string } | null} detected
   */
  function decide(detected) {
    const code = detected ? detected.country : '';
    const known = code !== '' && Object.prototype.hasOwnProperty.call(currencies, code);

    if (!known) {
      if (languageFallback) {
        const wantsPolish = (navigator.language || '').toLowerCase().startsWith('pl');
        if (store === 'eu' && wantsPolish) {
          open('PL', '');
          return;
        }
        if (store === 'pl' && !wantsPolish) {
          open('', '');
          return;
        }
      }
      sessionSet(SESSION_KEY);
      return;
    }

    if (code === current) {
      sessionSet(SESSION_KEY);
      return;
    }

    const home = code === 'PL' ? 'pl' : 'eu';
    if (home === store) {
      if (currencies[code] !== currency) {
        writeCookie(COOKIE, 'auto', days);
        submit(code, false);
      } else {
        sessionSet(SESSION_KEY);
      }
      return;
    }

    open(code, detected ? detected.name : '');
  }

  /**
   * Opens the dialog for the other store.
   * @param {string} code - detected country, or '' when unknown (browser-language fallback)
   * @param {string} name - the country name as the endpoint returned it (page language)
   */
  function open(code, name) {
    const country = code || (store === 'pl' ? fallbackCountry : 'PL');
    countryInput.value = country;

    const known = dialog.querySelector('[data-geo-title="known"]');
    const unknown = dialog.querySelector('[data-geo-title="unknown"]');
    if (code && known instanceof HTMLElement) {
      // The title keeps a literal {country} slot: Liquid's t filter escapes HTML, so no element.
      if (!known.dataset.template) known.dataset.template = known.textContent || '';
      known.textContent = known.dataset.template.replace('{country}', displayName(code, name, store === 'pl' ? 'en' : 'pl'));
    }
    if (known && unknown) {
      known.toggleAttribute('hidden', !code);
      unknown.toggleAttribute('hidden', !!code);
    }

    let flag = data.targetFlag || 'EU';
    if (store === 'pl' && ['GB', 'CH', 'NO'].includes(country)) flag = country;
    for (const svg of dialog.querySelectorAll('[data-geo-flag]')) {
      svg.toggleAttribute('hidden', svg.getAttribute('data-geo-flag') !== flag);
    }

    const stay = () => {
      writeCookie(COOKIE, 'stay', days);
      if (dialog.open) dialog.close();
    };
    for (const button of dialog.querySelectorAll('[data-geo-stay]')) {
      button.addEventListener('click', stay);
    }
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      stay();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) stay();
    });
    form.addEventListener('submit', () => {
      writeCookie(COOKIE, 'go', days);
      setReturnTo(true);
    });

    sessionSet(SESSION_KEY);
    window.requestAnimationFrame(() => {
      if (dialog.open) return;
      dialog.showModal();
      const go = dialog.querySelector('[data-geo-go]');
      if (go instanceof HTMLElement) go.focus();
    });
  }

  /**
   * Submits the localization form; Shopify applies the country and redirects to its market's
   * domain when needed.
   * @param {string} country
   * @param {boolean} marker - add the `geo` marker for the sibling store
   */
  function submit(country, marker) {
    countryInput.value = country;
    setReturnTo(marker);
    form.submit();
  }

  /**
   * @param {boolean} marker
   */
  function setReturnTo(marker) {
    const returnTo = form.elements.namedItem('return_to');
    if (!(returnTo instanceof HTMLInputElement)) return;
    const target = new URL(window.location.href);
    target.searchParams.delete('geo_debug');
    if (marker) target.searchParams.set(MARKER, '1');
    returnTo.value = target.pathname + target.search;
  }
}

/**
 * Asks Shopify which country the visitor's IP resolves to.
 * @param {string} exclude - the current country
 * @returns {Promise<{ country: string, name: string } | null>}
 */
async function detect(exclude) {
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const query = `?country[enabled]=true&country[exclude]=${encodeURIComponent(exclude)}`;
    const response = await fetch(`${root}browsing_context_suggestions.json${query}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    const json = await response.json();
    const country = json && json.detected_values && json.detected_values.country;
    if (!country || typeof country.handle !== 'string' || country.handle === '') return null;
    return { country: country.handle.toUpperCase(), name: typeof country.name === 'string' ? country.name : '' };
  } catch {
    return null;
  }
}

/**
 * @param {string} value - `?geo_debug=` value: a country code or `none`
 */
function simulated(value) {
  const code = value.trim().toUpperCase();
  if (code === '' || code === 'NONE') return null;
  return { country: code, name: '' };
}

/**
 * The country name in the dialog's language: Intl first, the endpoint's name as fallback.
 * @param {string} code
 * @param {string} fallback
 * @param {string} language
 */
function displayName(code, fallback, language) {
  // English country names that take an article in "in …".
  const article = { GB: 'the United Kingdom', NL: 'the Netherlands', CZ: 'the Czech Republic', US: 'the United States' };
  if (language === 'en' && article[code]) return article[code];
  try {
    const names = new Intl.DisplayNames([language], { type: 'region' });
    const name = names.of(code);
    if (name && name !== code) return name;
  } catch {
    // Intl.DisplayNames unsupported: use the fallback
  }
  return fallback || code;
}

/**
 * @param {string} value
 */
function originOf(value) {
  try {
    return value ? new URL(value).origin : '';
  } catch {
    return '';
  }
}

/**
 * @param {string} name
 */
function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
function writeCookie(name, value, days) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${Math.round(days * 86400)}; Path=/; SameSite=Lax${secure}`;
}

/**
 * @param {string} key
 */
function sessionGet(key) {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * @param {string} key
 */
function sessionSet(key) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // storage unavailable: the check simply repeats on the next page
  }
}
